import { useState, useRef, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { PersonSearchResult, ProjectReader, ReaderCatalogPrice } from '@/api/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { DropdownList, type DropdownListOption } from '@/components/ui/DropdownList/DropdownList';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { TimeInput } from '@/components/ui/TimeInput/TimeInput';
import { PriceFixCombo } from '@/components/ui/PriceFixCombo/PriceFixCombo';
import { Input } from '@/components/ui/Input/Input';
import {
  formatPriceInputDisplay,
  sortReaderCatalogPrices,
  formatReaderCatalogPriceLine,
} from '@/utils/formatNumber';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import { ComboEmptyCreatePanel } from '@/components/ui/ComboEmptyCreatePanel/ComboEmptyCreatePanel';
import './ReaderPriceList.css';

/** Нормализация для API: только полное чч:мм:сс или null (как у projects.time) */
export function normalizeReaderTimeForApi(timeInput: string): string | null {
  const t = timeInput.trim();
  if (!t) return null;
  return /^\d{2}:\d{2}:\d{2}$/.test(t) ? t : null;
}

export function isValidReaderTimeInput(timeInput: string): boolean {
  const t = timeInput.trim();
  if (!t) return true;
  return /^\d{2}:\d{2}:\d{2}$/.test(t);
}

export interface ReaderEntryItem {
  rowKey: string;
  id: string;
  name: string;
  /** Локальное поле ввода времени (чч:мм:сс) */
  time_input: string;
  /** Позиции стоимости из каталога чтеца */
  reader_prices: ReaderCatalogPrice[];
  /** Выбранная базовая позиция каталога для проекта */
  reader_catalog_price_id: string | null;
  price_input: string;
  is_price_fixed: boolean;
  /** Локальный ввод количества правок по чтецу (только UI попапа таблицы) */
  corrections_input: string;
}

function parsePriceInputToNumber(raw: string): number | null {
  const t = raw.trim().replace(',', '.');
  if (!t) return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

/** Целое число правок по чтецу в проекте для API (null — не указано). */
export function parseReaderCorrectionsInput(raw: string): number | null {
  const t = raw.trim().replace(/[^\d]/g, '');
  if (!t) return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Плейсхолдер суммы: база каталога; при нескольких позициях без выбора — «Не выбран» (см. пункт в списке). */
function readerPriceComboPlaceholder(item: ReaderEntryItem): string {
  const sorted = sortReaderCatalogPrices(item.reader_prices);
  if (!item.id || sorted.length === 0) return '0';
  if (sorted.length === 1) {
    const n = Number(sorted[0]!.price);
    return Number.isFinite(n) ? formatPriceInputDisplay(n) : '0';
  }
  if (!item.reader_catalog_price_id) return 'Не выбран';
  const p = sorted.find((x) => x.id === item.reader_catalog_price_id);
  if (!p) return 'Не выбран';
  const n = Number(p.price);
  return Number.isFinite(n) ? formatPriceInputDisplay(n) : 'Не выбран';
}

export function projectReadersToEntries(readers: ProjectReader[]): ReaderEntryItem[] {
  return readers.map((r) => ({
    rowKey: r.id,
    id: r.id,
    name: r.name,
    time_input: r.reader_time?.trim() ?? '',
    reader_prices: sortReaderCatalogPrices(r.reader_prices),
    reader_catalog_price_id: r.reader_catalog_price_id ?? null,
    price_input: r.reader_custom_price != null ? formatPriceInputDisplay(r.reader_custom_price) : '',
    is_price_fixed: r.is_reader_price_fixed ?? false,
    corrections_input: r.reader_edits_number != null ? String(r.reader_edits_number) : '',
  }));
}

export function entriesToReaderInputs(items: ReaderEntryItem[]) {
  return items
    .filter((item) => item.id)
    .map((item) => ({
      person_id: item.id,
      reader_time: normalizeReaderTimeForApi(item.time_input),
      reader_custom_price: parsePriceInputToNumber(item.price_input),
      is_reader_price_fixed: item.is_price_fixed,
      reader_catalog_price_id: item.reader_catalog_price_id?.trim() ? item.reader_catalog_price_id : null,
      reader_edits_number: parseReaderCorrectionsInput(item.corrections_input),
    }));
}

function makeEmptyEntry(): ReaderEntryItem {
  return {
    rowKey: Math.random().toString(36).slice(2),
    id: '',
    name: '',
    time_input: '',
    reader_prices: [],
    reader_catalog_price_id: null,
    price_input: '',
    is_price_fixed: false,
    corrections_input: '',
  };
}

/** Строка списка чтецов проекта из записи каталога (после создания чтеца и т.п.) */
export function personToReaderEntry(person: PersonSearchResult): ReaderEntryItem {
  const sorted = sortReaderCatalogPrices(person.reader_prices ?? []);
  const autoCatalogId = sorted.length === 1 ? sorted[0]!.id : null;
  return {
    rowKey: Math.random().toString(36).slice(2),
    id: person.id,
    name: person.name,
    time_input: '',
    reader_prices: sorted,
    reader_catalog_price_id: autoCatalogId,
    price_input: '',
    is_price_fixed: false,
    corrections_input: '',
  };
}

export const readerCatalogToEntry = personToReaderEntry;

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

interface ReaderEntryRowProps {
  item: ReaderEntryItem;
  /** id чтецов в других строках — не показывать в подсказках */
  excludeReaderIds: string[];
  onChange: (updated: ReaderEntryItem) => void;
  onRemove: () => void;
  onReaderSelect?: (reader: PersonSearchResult) => void;
  debounceFlushKey?: string | number;
  /** Поле «Правки» (только попап редактирования ячейки в таблице проектов) */
  showReaderCorrections?: boolean;
  hidePrices?: boolean;
}

function ReaderEntryRow({
  item,
  excludeReaderIds,
  onChange,
  onRemove,
  onReaderSelect,
  debounceFlushKey,
  showReaderCorrections = false,
  hidePrices = false,
}: ReaderEntryRowProps) {
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const [searchInput, setSearchInput] = useState(item.name);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [catalogDropdownOpen, setCatalogDropdownOpen] = useState(false);
  const [catalogHighlightIndex, setCatalogHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const priceComboWrapRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef(item);
  itemRef.current = item;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onReaderSelectRef = useRef(onReaderSelect);
  onReaderSelectRef.current = onReaderSelect;
  const debouncedSearch = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS, debounceFlushKey);
  const dropdownStyle = useDropdownPosition(containerRef, dropdownOpen);

  const catalogPricesSorted = sortReaderCatalogPrices(item.reader_prices);

  const catalogDropdownOptions = useMemo((): DropdownListOption<ReaderCatalogPrice | null>[] => {
    return [
      { value: null, label: 'Не выбран', key: '__catalog-none' },
      ...catalogPricesSorted.map((p) => ({
        value: p,
        label: formatReaderCatalogPriceLine(p),
        key: p.id,
      })),
    ];
  }, [catalogPricesSorted]);

  const { data: searchResponse, isFetching } = useQuery({
    queryKey: ['persons', debouncedSearch],
    queryFn: async () => {
      return api.persons.list(debouncedSearch, { limit: 25, offset: 0 });
    },
    enabled: dropdownOpen && debouncedSearch.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const rawResults = searchResponse?.items ?? [];
  const searchResults = rawResults.filter((r) => !excludeReaderIds.includes(r.id));

  useEffect(() => {
    setSearchInput(item.name);
  }, [item.name]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [searchResults.length]);

  useEffect(() => {
    setCatalogHighlightIndex(0);
  }, [catalogPricesSorted.length]);

  useClickOutside(containerRef, () => setDropdownOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });
  useClickOutside(priceComboWrapRef, () => setCatalogDropdownOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });

  const selectCatalogRow = useCallback(
    (row: ReaderCatalogPrice | null) => {
      if (row) {
        onChange({
          ...item,
          reader_catalog_price_id: row.id,
          price_input: '',
          is_price_fixed: false,
        });
      } else {
        onChange({
          ...item,
          reader_catalog_price_id: null,
          price_input: '',
          is_price_fixed: false,
        });
      }
      setCatalogDropdownOpen(false);
    },
    [item, onChange]
  );

  const selectReader = useCallback(
    (reader: PersonSearchResult) => {
      const sorted = sortReaderCatalogPrices(reader.reader_prices ?? []);
      const autoCatalogId = sorted.length === 1 ? sorted[0]!.id : null;
      onChange({
        ...item,
        id: reader.id,
        name: reader.name,
        time_input: '',
        reader_prices: sorted,
        reader_catalog_price_id: autoCatalogId,
        price_input: '',
        is_price_fixed: false,
        corrections_input: '',
      });
      setSearchInput(reader.name);
      setDropdownOpen(false);
      onReaderSelect?.(reader);
    },
    [item, onChange, onReaderSelect]
  );

  const openCreateReader = useCallback(() => {
    const q = searchInput.trim();
    setDropdownOpen(false);
    openCatalogManage({
      tab: 'persons',
      rolePreset: 'reader',
      nameSeed: q,
      onPersonCreated: (payload) => {
        const created = payload.readers[0];
        const asSearch: PersonSearchResult = created
          ? {
              id: created.id,
              name: created.name,
              first_name: created.first_name,
              last_name: created.last_name,
              reader_prices: created.reader_prices ?? [],
            }
          : {
              id: payload.person.id,
              name: payload.person.name,
              first_name: payload.person.first_name,
              last_name: payload.person.last_name,
              reader_prices: [],
            };
        const sorted = sortReaderCatalogPrices(asSearch.reader_prices ?? []);
        const autoCatalogId = sorted.length === 1 ? sorted[0]!.id : null;
        const current = itemRef.current;
        onChangeRef.current({
          ...current,
          id: asSearch.id,
          name: asSearch.name,
          time_input: '',
          reader_prices: sorted,
          reader_catalog_price_id: autoCatalogId,
          price_input: '',
          is_price_fixed: false,
          corrections_input: '',
        });
        setSearchInput(asSearch.name);
        onReaderSelectRef.current?.(asSearch);
      },
    });
  }, [searchInput, openCatalogManage]);

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    if (value.trim().length > 0) {
      setDropdownOpen(true);
    } else {
      setDropdownOpen(false);
      onChange({
        ...item,
        id: '',
        name: '',
        time_input: '',
        reader_prices: [],
        reader_catalog_price_id: null,
        price_input: '',
        is_price_fixed: false,
        corrections_input: '',
      });
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!dropdownOpen) {
      if (e.key === 'ArrowDown' && searchInput.trim().length >= MIN_SEARCH_LENGTH) setDropdownOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = searchResults[highlightIndex];
      if (opt) selectReader(opt);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const openCatalogPicker = useCallback(() => {
    const sorted = sortReaderCatalogPrices(item.reader_prices);
    if (!item.id || sorted.length <= 1) return;
    if (!item.reader_catalog_price_id) {
      setCatalogHighlightIndex(0);
    } else {
      const idx = sorted.findIndex((p) => p.id === item.reader_catalog_price_id);
      setCatalogHighlightIndex(idx >= 0 ? idx + 1 : 0);
    }
    setCatalogDropdownOpen(true);
  }, [item.id, item.reader_catalog_price_id, item.reader_prices]);

  const handleCatalogKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const canOpen = !!item.id && catalogPricesSorted.length > 1;
    if (!catalogDropdownOpen) {
      if (e.key === 'ArrowDown' && canOpen) {
        e.preventDefault();
        openCatalogPicker();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCatalogHighlightIndex((i) => Math.min(i + 1, catalogPricesSorted.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCatalogHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = catalogDropdownOptions[catalogHighlightIndex]?.value;
      if (opt === undefined) return;
      selectCatalogRow(opt);
    } else if (e.key === 'Escape') {
      setCatalogDropdownOpen(false);
    }
  };

  const catalogPickEnabled = !!item.id && catalogPricesSorted.length > 1;

  return (
    <div
      className={`reader-entry-row reader-entry-row_project${showReaderCorrections ? ' reader-entry-row_project--readers-popover' : ''}`}
    >
      <div ref={containerRef} className="reader-entry-row__reader-field">
        <input
          className="reader-entry-row__reader-input"
          value={searchInput}
          onChange={(e) => handleSearchInput(e.target.value)}
          onFocus={() => {
            if (searchInput.trim().length > 0) setDropdownOpen(true);
          }}
          onBlur={() => setDropdownOpen(false)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Чтец"
          autoComplete="off"
        />
        <ComboClearButton onClick={onRemove} />
        {dropdownOpen &&
          searchInput.trim().length > 0 &&
          (searchInput.trim().length < MIN_SEARCH_LENGTH ? (
            createPortal(
              <div className="reader-entry-row__hint-dropdown" data-combo-dropdown style={dropdownStyle}>
                <div className="reader-entry-row__hint">Введите минимум 2 символа для поиска</div>
              </div>,
              document.body
            )
          ) : searchResults.length === 0 && isFetching ? (
            createPortal(
              <div className="reader-entry-row__hint-dropdown" data-combo-dropdown style={dropdownStyle}>
                <div className="reader-entry-row__hint">Загрузка…</div>
              </div>,
              document.body
            )
          ) : searchResults.length === 0 ? (
            createPortal(
              <div className="reader-entry-row__hint-dropdown" data-combo-dropdown style={dropdownStyle}>
                <ComboEmptyCreatePanel
                  actions={
                    canManageCatalog
                      ? [
                          {
                            id: '__create_reader',
                            label: 'Создать чтеца',
                            onSelect: openCreateReader,
                          },
                        ]
                      : []
                  }
                />
              </div>,
              document.body
            )
          ) : (
            <DropdownList<PersonSearchResult>
              triggerRef={containerRef}
              open={dropdownOpen}
              options={searchResults.map((o) => ({ value: o, label: o.name, key: o.id }))}
              highlightIndex={highlightIndex}
              onSelect={selectReader}
              onHighlightChange={setHighlightIndex}
            />
          ))}
      </div>

      {!hidePrices ? (
      <div ref={priceComboWrapRef} className="reader-entry-row__price-combo-wrap">
        <PriceFixCombo
          className="reader-entry-row__price-fix-combo"
          value={item.price_input}
          onValueChange={(price_input) => onChange({ ...item, price_input })}
          isFixed={item.is_price_fixed}
          onFixedChange={(is_price_fixed) => onChange({ ...item, is_price_fixed })}
          placeholder={readerPriceComboPlaceholder(item)}
          disabled={!item.id}
          onInputFocus={() => {
            if (catalogPickEnabled) openCatalogPicker();
          }}
          onInputBlur={() => setCatalogDropdownOpen(false)}
          onInputKeyDown={handleCatalogKeyDown}
        />
        {catalogDropdownOpen && catalogPickEnabled ? (
          <DropdownList<ReaderCatalogPrice | null>
            triggerRef={priceComboWrapRef}
            open={catalogDropdownOpen}
            options={catalogDropdownOptions}
            highlightIndex={catalogHighlightIndex}
            onSelect={(p) => selectCatalogRow(p)}
            onHighlightChange={setCatalogHighlightIndex}
          />
        ) : null}
      </div>
      ) : null}

      {showReaderCorrections ? (
        <div className="reader-entry-row__corrections-wrap">
          <Input
            value={item.corrections_input}
            onChange={(e) => onChange({ ...item, corrections_input: e.target.value.replace(/[^\d]/g, '') })}
            placeholder="Правки"
            title="Количество правок по чтецу"
            fullWidth
            inputMode="numeric"
            disabled={!item.id}
          />
        </div>
      ) : null}

      <div className="reader-entry-row__time-wrap">
        <TimeInput
          value={item.time_input}
          onChange={(v) => onChange({ ...item, time_input: v })}
          placeholder="00:00:00"
          title="Время по чтецу"
          fullWidth={false}
          inputClassName="reader-entry-row__time-input-inner"
          autoComplete="off"
        />
      </div>
    </div>
  );
}

interface ReaderPriceListProps {
  value: ReaderEntryItem[];
  onChange: (entries: ReaderEntryItem[]) => void;
  onReaderSelect?: (reader: PersonSearchResult) => void;
  /** Рядом с кнопкой «+ Добавить чтеца» (например «новая персона» в модалке проекта) */
  trailingSlot?: ReactNode;
  debounceFlushKey?: string | number;
  /** Поле «Правки» по чтецу — только попап редактирования ячейки таблицы */
  showReaderCorrections?: boolean;
  hidePrices?: boolean;
}

export function ReaderPriceList({
  value,
  onChange,
  onReaderSelect,
  trailingSlot,
  debounceFlushKey,
  showReaderCorrections = false,
  hidePrices = false,
}: ReaderPriceListProps) {
  const updateRow = (idx: number, updated: ReaderEntryItem) => {
    const next = [...value];
    next[idx] = updated;
    onChange(next);
  };

  const removeRow = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    onChange([...value, makeEmptyEntry()]);
  };

  return (
    <div className="reader-price-list">
      {value.map((item, idx) => {
        const excludeReaderIds = value
          .filter((_, i) => i !== idx)
          .map((r) => r.id)
          .filter(Boolean);
        return (
          <ReaderEntryRow
            key={item.rowKey}
            item={item}
            excludeReaderIds={excludeReaderIds}
            onChange={(updated) => updateRow(idx, updated)}
            onRemove={() => removeRow(idx)}
            onReaderSelect={onReaderSelect}
            debounceFlushKey={debounceFlushKey}
            showReaderCorrections={showReaderCorrections}
            hidePrices={hidePrices}
          />
        );
      })}
      <div className="reader-price-list__add-row">
        <button type="button" className="reader-price-list__add" onClick={addRow}>
          + Добавить чтеца
        </button>
        {trailingSlot}
      </div>
    </div>
  );
}
